// The list-screen grammar from Web Portal v2.
//
// The design drives ~22 list screens off one renderer: an optional summary
// strip, a chip filter row with a result count, then a table whose cells are
// typed rather than individually styled. Ours were hand-rolled per page, so
// columns, filters and typography drifted from each other and from the
// design — thirteen screens, thirteen slightly different tables.
//
// This is that renderer. A page declares its columns and rows; the shapes
// come from here.

import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/cn';

/**
 * Cell types, matching the design's `CT` map.
 *
 * Typing the cell rather than styling it per page is what keeps thirteen
 * tables looking like one product: a reference is always mono, money is
 * always right-aligned and tabular, a status is always a pill.
 */
export type CellType = 'text' | 'strong' | 'muted' | 'mono' | 'money' | 'pill' | 'avatar' | 'node';

export interface Column<T> {
  key: string;
  header: string;
  type?: CellType;
  /** CSS grid track. Defaults to a flexible minmax. */
  width?: string;
  value: (row: T) => ReactNode;
}

export interface SummaryTile {
  label: string;
  value: string;
  hint?: string;
}

export interface FilterChip {
  key: string;
  label: string;
}

const CELL: Record<Exclude<CellType, 'pill' | 'avatar' | 'node'>, string> = {
  text: 'text-[13px] font-semibold text-[var(--color-body)]',
  strong: 'text-[13.5px] font-extrabold text-[var(--color-ink)]',
  muted: 'text-[12.5px] font-medium text-[var(--color-muted)]',
  mono: 'font-mono text-[12.5px] font-medium text-[var(--color-body)]',
  money: 'text-[13.5px] font-extrabold tabular-nums text-[var(--color-ink)] text-right',
};

/** Initials badge + name, for the design's `avatar` cell. */
export function AvatarCell({ name }: { name: string | null }) {
  if (!name) return <span className="text-[var(--color-muted)]">—</span>;

  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');

  return (
    <span className="inline-flex items-center gap-2">
      <span className="inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full bg-[var(--color-tint)] text-[11px] font-extrabold text-brand-600">
        {initials || '?'}
      </span>
      <span className="truncate text-[13.5px] font-bold text-[var(--color-ink)]">{name}</span>
    </span>
  );
}

interface Props<T> {
  title: string;
  subtitle?: string;
  /** Primary action, top-right beside the title. */
  action?: { label: string; to?: string; onClick?: () => void };
  /** Extra header controls beside the primary action. */
  secondaryActions?: ReactNode;
  summary?: SummaryTile[];
  filters?: FilterChip[];
  activeFilter?: string;
  onFilter?: (key: string) => void;
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string | number;
  /** Where a row click goes, when rows are navigable. */
  rowHref?: (row: T) => string;
  loading?: boolean;
  empty?: ReactNode;
  /**
   * An expanded panel under a row — delivery proof, an inline editor.
   * Returning null leaves the row alone.
   */
  renderExpanded?: (row: T) => ReactNode;
  /**
   * A phone-width card for one row.
   *
   * Without this a wide table becomes horizontal scroll on a phone, which is
   * usable but poor. Three pages already shipped a `sm:hidden` card stack and
   * would have lost it in the conversion.
   */
  renderCard?: (row: T) => ReactNode;
}

export function DataTable<T>({
  title, subtitle, action, secondaryActions, summary, filters, activeFilter, onFilter,
  columns, rows, rowKey, rowHref, loading, empty, renderExpanded, renderCard,
}: Props<T>) {
  const template = columns.map((c) => c.width ?? 'minmax(0,1fr)').join(' ');
  // Below this the grid stops being readable and horizontal scroll is the
  // honest answer — squeezing eight columns onto a phone helps nobody.
  const minWidth = Math.max(560, columns.length * 120);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-[20px] font-extrabold tracking-[-0.5px] text-[var(--color-ink)]">{title}</h2>
          {subtitle && <p className="mt-0.5 text-[13.5px] text-[var(--color-secondary-2)]">{subtitle}</p>}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {secondaryActions}
          {action && (
          action.to ? (
            <Link to={action.to} className="shrink-0 rounded-xl bg-[var(--color-interactive)] px-4 py-2.5 text-[13px] font-extrabold text-white hover:bg-brand-600 transition-colors">
              {action.label}
            </Link>
          ) : (
            <button type="button" onClick={action.onClick} className="shrink-0 rounded-xl bg-[var(--color-interactive)] px-4 py-2.5 text-[13px] font-extrabold text-white hover:bg-brand-600 transition-colors">
              {action.label}
            </button>
          )
          )}
        </div>
      </div>

      {/* One joined card with divider rules, not a row of separate cards —
          these are facets of one total, and the design groups them to say so. */}
      {summary && summary.length > 0 && (
        <section className="grid rounded-[22px] border border-[var(--color-border)] bg-white grid-cols-[repeat(auto-fit,minmax(min(100%,200px),1fr))]">
          {summary.map((t, i) => (
            <div key={t.label} className={cn('px-[22px] py-5', i && 'border-l border-[var(--color-hairline)]')}>
              <p className="text-[12.5px] font-semibold text-[var(--color-secondary-2)]">{t.label}</p>
              <p className="mt-1.5 text-[24px] font-extrabold tracking-[-0.7px] tabular-nums text-[var(--color-ink)]">{t.value}</p>
              {t.hint && <p className="mt-0.5 truncate text-[12px] text-[var(--color-muted)]">{t.hint}</p>}
            </div>
          ))}
        </section>
      )}

      <section className="overflow-hidden rounded-[22px] border border-[var(--color-border)] bg-white">
        {/* Chips, not a <select>. A dropdown hides the option set; the design
            shows every filter and which one is on. */}
        {(filters?.length || rows.length > 0) && (
          <div className="flex flex-wrap items-center gap-2 border-b border-[var(--color-hairline)] px-4 py-3">
            {filters?.map((f) => (
              <button
                key={f.key}
                type="button"
                aria-pressed={activeFilter === f.key}
                onClick={() => onFilter?.(f.key)}
                className={cn(
                  'rounded-[9px] px-3 py-[7px] text-[12.5px] font-extrabold transition-colors',
                  activeFilter === f.key
                    ? 'bg-[var(--color-ink)] text-white'
                    : 'text-[var(--color-secondary)] hover:text-[var(--color-ink)]',
                )}
              >
                {f.label}
              </button>
            ))}
            {!loading && (
              <span className="ml-auto text-[12.5px] text-[var(--color-muted)]">
                {rows.length} {rows.length === 1 ? 'result' : 'results'}
              </span>
            )}
          </div>
        )}

        {loading ? (
          <div className="px-4 py-10 text-center text-[13px] text-[var(--color-muted)]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="px-4 py-10 text-center text-[13px] text-[var(--color-muted)]">
            {empty ?? 'Nothing matches this filter.'}
          </div>
        ) : (
          <>
          {/* A phone gets cards when the page supplies them; a wide grid on a
              360px screen is horizontal scrolling, which is a last resort not
              a layout. */}
          {renderCard && (
            <div className="divide-y divide-[#F2F4F9] sm:hidden">
              {rows.map((row) => (
                <div key={`card-${rowKey(row)}`} className="px-4 py-3">
                  {renderCard(row)}
                </div>
              ))}
            </div>
          )}

          <div className={cn('overflow-x-auto', renderCard && 'hidden sm:block')}>
            <div style={{ minWidth }}>
              <div
                className="grid gap-3 bg-[#FAFBFE] px-[22px] py-2.5 text-[11px] font-extrabold uppercase tracking-[0.7px] text-[var(--color-muted)]"
                style={{ gridTemplateColumns: template }}
              >
                {columns.map((c) => (
                  <div key={c.key} className={cn(c.type === 'money' && 'text-right')}>{c.header}</div>
                ))}
              </div>

              {rows.map((row) => {
                const cells = columns.map((c) => (
                  <div
                    key={c.key}
                    className={cn(
                      'min-w-0 truncate',
                      c.type && c.type in CELL && CELL[c.type as keyof typeof CELL],
                    )}
                  >
                    {c.value(row)}
                  </div>
                ));

                const inner = (
                  <div
                    className="grid items-center gap-3 border-t border-[#F2F4F9] px-[22px] py-3"
                    style={{ gridTemplateColumns: template }}
                  >
                    {cells}
                  </div>
                );

                const expanded = renderExpanded?.(row);

                return (
                  <div key={rowKey(row)}>
                    {rowHref ? (
                      <Link to={rowHref(row)} className="block hover:bg-[var(--color-tint-3)] transition-colors">
                        {inner}
                      </Link>
                    ) : inner}
                    {/* Delivery proof, an inline editor — anything that belongs
                        to the row but will not fit in a cell. */}
                    {expanded && (
                      <div className="border-t border-[#F2F4F9] bg-[var(--color-tint-3)] px-[22px] py-3">
                        {expanded}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          </>
        )}
      </section>
    </div>
  );
}
